import _ from "lodash";
import React, { useRef, useEffect, memo, useState } from "react";
import classNames from "classnames";
import classes from "./DraggableCell.scss";
import PropTypes from "prop-types";
import InlineEditor from "UIComponents/InlineEditor";
import { Checkbox, TextAreaInput } from "@toddle-edu/ds-web";
import { COL_CHECKBOX } from "UnitPlans/components/IBcomponents/ActionTableV2/ActionTableV2Utils";
import { Editor, Transforms } from "slate";
import { ReactEditor } from "slate-react";
import ACLStore from "lib/aclStore";
import { Sentry } from "services/sentry";

const style = {
  textAreaStyles: {
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
    height: "auto",
    font: "inherit",
    padding: "0px",
    borderRadius: "0px",
    overflow: "hidden",
  },
  checkboxStyle: {
    pointerEvents: "none",
  },
  disabledCheckboxStyle: {
    cursor: "not-allowed",
  },
  enabledCheckboxStyle: {
    cursor: "default",
  },
};

const TableCell = ({
  cell,
  setCellValue,
  mode,
  colIndex,
  rowIndex,
  setShowColumnOptions,
  setShowRowOptions,
  showColumnOptions,
  editorToolbarOptions,
  testId,
  tableCellStyles,
  setNodeRef,
  isHeader = false,
  header,
  handleColumnResize,
  setHeaderCellInputRefs,
  isColumnDragging,
  isLastRow,
  onFocusInputCell,
  onBlurInputCell,
  resizingColumnId,
}) => {
  const totalColumns = _.get(cell.getContext(), ["table"])?.getFlatHeaders()
    ?.length;

  const columnId = _.get(cell, ["column", "id"]);
  const rowId = _.get(cell, ["row", "id"]);

  const isResizing = _.isEqual(resizingColumnId, columnId);

  const accessorKey = _.get(cell, ["column", "columnDef", "accessorKey"]);

  const isCheckbox = _.isEqual(accessorKey, COL_CHECKBOX);
  const isCreateMode = _.isEqual(mode, "create");

  const CellTag = isHeader ? "th" : "td";

  const cellVal = cell.getValue();
  const isDisabled = mode === "view";

  // reference for input cell, to get clickOutside event
  const cellRef = useRef();
  const inputRef = useRef();

  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    // This use effect will run when click on any cell expect drag handle, to close column and row options overlay.
    function mouseDown(e) {
      setShowColumnOptions(-1);
      setShowRowOptions(-1);
    }
    if (cellRef && cellRef?.current) {
      cellRef?.current?.addEventListener("mousedown", mouseDown);
    }
    return () => {
      if (cellRef && cellRef?.current) {
        cellRef?.current?.removeEventListener("mousedown", mouseDown);
      }
    };
  }, [cellRef?.current]);

  const isColDraggingOrShowColumnOptions =
    isColumnDragging || _.isEqual(columnId, showColumnOptions);

  const headerClassnames = classNames({
    [classes.tableCell]: true,
    [classes.tableCellActive]: isColDraggingOrShowColumnOptions,
    [classes.header]: isHeader,
    ["text-label"]: isHeader,
    ["text-body"]: !isHeader,

    [classes.tableCellActiveFirstRow]:
      isColDraggingOrShowColumnOptions && isHeader,
    [classes.tableCellActiveLastRow]:
      isColDraggingOrShowColumnOptions && isLastRow,
    [classes.topLeftCell]: isHeader && colIndex === 0,
    [classes.topRightCell]: isHeader && colIndex === totalColumns - 1,
    [classes.bottomLeftCell]:
      rowIndex === _.size(cell.getContext().table.options.data) - 1 &&
      colIndex === 0,
    [classes.bottomRightCell]:
      rowIndex === _.size(cell.getContext().table.options.data) - 1 &&
      colIndex === totalColumns - 1,
    [classes.focusedCell]: isFocused,
    [classes.resizing]: isResizing,
  });

  const onChangeInput = value => {
    setCellValue({
      rowId,
      colId: accessorKey,
      val: value,
    });
  };

  const onCheckboxClick = e => {
    if (!isCreateMode) {
      return;
    }

    setCellValue({
      rowId,
      colId: accessorKey,
      val: e.target.checked,
    });
  };

  const cellStyles = {
    ...tableCellStyles,
    ...(!header && { width: cell.column.getSize() }),
  };

  const onResizingStart = e => {
    // to prevent the click event on the reSizer to be propagated to the cell
    e.stopPropagation();
    // did to handle case when the cursor is far from the resizer while releasing the click
    document.addEventListener("mouseup", () => handleColumnResize(), {
      once: true,
    });
    header.getResizeHandler()(e);
  };

  const renderCheckbox = () => {
    return !_.isNull(cellVal) ? (
      <div
        className={classes.checkbox}
        style={
          isCreateMode
            ? style.enabledCheckboxStyle
            : style.disabledCheckboxStyle
        }
      >
        <Checkbox
          isChecked={cellVal}
          onChange={onCheckboxClick}
          dsVersion="2.0"
          style={!isCreateMode ? style.checkboxStyle : {}}
        />
      </div>
    ) : null;
  };

  const showResizeHandler = isHeader && header && !isCheckbox && !isDisabled;

  const handleMouseDown = e => {
    if (isDisabled || !cellRef.current?.contains(e.target)) return;
    const editor = inputRef?.current?.getEditor?.() ?? null;

    if (editor) {
      // Check if there's an active selection in the browser
      const selection = window.getSelection();
      // If there's a selection and it's not collapsed (text is selected), let it behave natively
      if (selection && !selection.isCollapsed) {
        return;
      }
      // If the editor is already focused, preserve the current selection or caret position
      // applied specifically when we are already there in the editor and clicking on the cell
      if (ReactEditor.isFocused(editor)) {
        return;
      }

      // Focus the editor, clearing any old selection, and place the caret at the end
      setTimeout(() => {
        try {
          if (editor) {
            ReactEditor.focus(editor);
            Transforms.deselect(editor); // Clear any previous selection
            const endPoint = Editor.end(editor, []);
            Transforms.select(editor, endPoint); // Move caret to the end
          }
        } catch (error) {
          if (ACLStore.can("FeatureFlag:ProjectsSentryDebugLog")) {
            // eslint-disable-next-line @toddle-edu/common/no-sentry-capture-exception
            Sentry.captureException(
              new Error("PROJECTS_ACTION_PLAN_SLATE_ERROR"),
              {
                level: "info",
                extra: {
                  editor,
                  error,
                },
              }
            );
          }
        }
      }, 0); // Delay to ensure native interactions are not disrupted
    } else if (inputRef.current) {
      const textAreaRef = inputRef.current;
      if (document.activeElement === textAreaRef) return;
      setTimeout(() => {
        textAreaRef.focus();
        const endPosition = textAreaRef.value.length;
        textAreaRef.setSelectionRange(endPosition, endPosition);
      }, 0);
    }
  };

  const onFocusTableInputCell = () => {
    if (isDisabled) return;
    setIsFocused(true);
    onFocusInputCell();
  };

  const onBlurTableInputCell = () => {
    setIsFocused(false);
    onBlurInputCell();
  };

  return (
    <CellTag
      style={cellStyles}
      ref={node => {
        if (setNodeRef) setNodeRef(node);
        cellRef.current = node;
      }}
      className={headerClassnames}
      data-test-id={`${testId}-draggableCell-tableCell`}
      key={cell.id}
      onMouseDown={handleMouseDown}
    >
      {isCheckbox ? (
        renderCheckbox()
      ) : isHeader ? (
        <TextAreaInput
          name="header"
          value={cellVal}
          onChange={e => onChangeInput(e.target.value)}
          style={style.textAreaStyles}
          minRows={1}
          ref={ref => {
            inputRef.current = ref;
            setHeaderCellInputRefs({ columnId, ref });
          }}
          readOnly={isDisabled}
          onFocus={onFocusTableInputCell}
          onBlur={onBlurTableInputCell}
        />
      ) : (
        <InlineEditor
          editorStyles={{
            height: "100%",
            overflowWrap: "anywhere",
            display: "block",
            outline: "none",
          }}
          onChange={value => {
            !isDisabled ? onChangeInput(value) : null;
          }}
          value={_.isNil(cellVal) ? "" : cellVal}
          mode={isDisabled ? "view" : "edit"}
          editorToolbarOptions={editorToolbarOptions}
          onFocusInputCell={onFocusTableInputCell}
          onBlurInputCell={onBlurTableInputCell}
          customRef={inputRef}
          shouldAllowOnDOMBeforeInput={false}
        />
      )}
      {showResizeHandler ? (
        <div
          onMouseDown={onResizingStart}
          onTouchStart={onResizingStart}
          className={classes.reSizer}
        />
      ) : null}
    </CellTag>
  );
};

TableCell.defaultProps = {
  tableCellStyles: {},
  isHeader: false,
  isColumnDragging: false,
  isLastRow: false,
  setHeaderCellInputRefs: _.noop,
};

TableCell.propTypes = {
  tableCellStyles: PropTypes.object,
  isHeader: PropTypes.bool,
  isColumnDragging: PropTypes.bool,
  isLastRow: PropTypes.bool,
  setHeaderCellInputRefs: PropTypes.func,
};

TableCell.displayName = "TableCell";

export default memo(TableCell);
